// src/pages/doctorPages/RecordType/DoctorRecordType.jsx
"use client";

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { message, Spin, Alert } from "antd";

import AppLayoutDoctor from "../../../navigation/Layout/LayoutDoctor";
import DoctorScrollTabs from "../../../Profile/components/ScrollTabs/DoctorScrollTabs";
import RecordListPanel from "../../components/RecordListPanel/RecordListPanel";
import RecordViewerPanel from "../../components/RecordViewerPanel/RecordViewerPanel";

import useExtraction from "../../hooks/useExtraction";
import { fetchDocumentsByType } from "../../redux/thunks/extractionThunks";
import { resetDocumentsByType } from "../../redux/slices/extractionSlice";
import { moveDocumentToTrash } from "../../redux/thunks/trashThunks";
import useMedicalCapabilities from "../../../authorization/useMedicalCapabilities";

import "./Doctorrecordtype.css";

const RECORD_TYPES = [
  { id: "BILAN_BIOCHIMIQUE", label: "Biochemistry Tests",  description: "Blood tests and biochemical analysis results" },
  { id: "IRM",              label: "MRI Scans",            description: "Magnetic resonance imaging scans" },
  { id: "ECHOGRAPHIE",      label: "Medical Ultrasound",   description: "Ultrasound examination results" },
  { id: "RADIOGRAPHIE",     label: "X-Ray Reports",        description: "Radiography and X-ray results" },
  { id: "ORDONNANCE",       label: "Prescriptions",        description: "Medical prescriptions" },
  { id: "COMPTE_RENDU",     label: "Medical Reports",      description: "Detailed medical reports" },
  { id: "AUTRE",            label: "Other Documents",      description: "Other medical documents" },
];

const TYPE_TO_COLLECTION = {
  BILAN_BIOCHIMIQUE: "bilan",
  IRM:               "irm",
  ECHOGRAPHIE:       "ecographie",
  RADIOGRAPHIE:      "rapport",
  ORDONNANCE:        "ordonnance",
  COMPTE_RENDU:      "compte_rendu",
  AUTRE:             "autre",
};

export default function DoctorRecordType() {
  // ✅ route: /doctor/patients/:patientId/records/:type
  const { patientId, type } = useParams();
  const navigate   = useNavigate();
  const dispatch   = useDispatch();
  const { pathname } = useLocation();

  // ✅ Même slice séparé que le patient — jamais pollué par myDocuments
  const documentsByType        = useSelector((s) => s.extraction?.documentsByType        ?? []);
  const documentsByTypeLoading = useSelector((s) => s.extraction?.documentsByTypeLoading ?? false);
  const doctorUser = useSelector((state) => state.doctor?.user || null);
  const doctorId =
    doctorUser?.id || doctorUser?.doctorId || doctorUser?.pgId || doctorUser?._id || null;
  const { capabilities, refresh: refreshCapabilities } =
    useMedicalCapabilities(patientId, doctorId);
  const canUpdateDocument = Boolean(capabilities?.medicalDocuments?.update);
  const canArchiveDocument = Boolean(capabilities?.medicalDocuments?.archive);

  const { isHealthy } = useExtraction(patientId, { autoLoad: false });

  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedRecord,   setSelectedRecord]   = useState(null);

  const medicalrecordRoot = useMemo(
    () => patientId
      ? `/doctor/patients/${patientId}/DoctorMedicalRecordPage`
      : "/doctor/patients",
    [patientId]
  );

  const forcedTabKey = "medicalrecord";
  const activeKey    = useMemo(() => pathname || forcedTabKey, [pathname]);

  // ✅ Reset au montage — évite les résidus de navigation précédente
  useEffect(() => {
    dispatch(resetDocumentsByType());
  }, [dispatch]);

  // ✅ Résoudre le type depuis les params
  useEffect(() => {
    const record = RECORD_TYPES.find(
      (r) => r.id === String(type || "").toUpperCase()
    );
    if (record) {
      setSelectedRecord(record);
    } else {
      message.error("Invalid document type");
      navigate(medicalrecordRoot, { replace: true });
    }
  }, [type, navigate, medicalrecordRoot]);

  // ✅ Charger uniquement les docs du bon type pour ce patient
  useEffect(() => {
    if (patientId && selectedRecord && isHealthy) {
      dispatch(
        fetchDocumentsByType({
          type:   selectedRecord.id,
          skip:   0,
          limit:  50,
          userId: patientId,
        })
      );
    }
  }, [dispatch, patientId, selectedRecord, isHealthy]);

  // ✅ Refresh après delete
  const refreshList = useCallback(() => {
    if (patientId && selectedRecord) {
      dispatch(
        fetchDocumentsByType({
          type:   selectedRecord.id,
          skip:   0,
          limit:  50,
          userId: patientId,
        })
      );
    }
  }, [dispatch, patientId, selectedRecord]);

  // Archivage uniquement : aucun hard delete n'est exposé au médecin.
  const handleDelete = useCallback(async (item) => {
    let docId  = null;
    let docData = null;

    if (item?.rawData) {
      docData = item.rawData;
      docId   = docData._id || docData.id;
    } else if (item?.id || item?._id) {
      docId   = item.id || item._id;
      docData = item;
    } else {
      message.error("Cannot delete: Invalid document");
      return;
    }

    if (!docId) {
      message.error("Cannot delete: Missing document ID");
      return;
    }

    let collection =
      docData?.collection ??
      (docData?.type_document
        ? TYPE_TO_COLLECTION[docData.type_document] ||
          String(docData.type_document).toLowerCase()
        : TYPE_TO_COLLECTION[selectedRecord?.id] ||
          String(selectedRecord?.id || "autre").toLowerCase());

    collection = String(collection)
      .toLowerCase()
      .replace(/-/g, "_")
      .replace(/\s+/g, "_");

    try {
      if (!item?.result) {
        await dispatch(
          moveDocumentToTrash({
            collection,
            documentId: docId,
            userId: patientId,
            version: docData?.version,
          }),
        ).unwrap();
      }
      if ((selectedDocument?.id || selectedDocument?._id) === docId) {
        setSelectedDocument(null);
      }
      refreshList();
    } catch (error) {
      if (error?.status === 403 || error?.response?.status === 403) {
        refreshCapabilities();
      }
      message.error(error?.message || error || "Error archiving document");
    }
  }, [dispatch, patientId, refreshCapabilities, refreshList, selectedDocument, selectedRecord]);

  const handleDocumentClick = useCallback((doc) => {
    setSelectedDocument(doc);
  }, []);

  const handleDownload = useCallback((doc) => {
    const fileUrl = doc?.chemin_fichier || doc?.file_path;
    if (!fileUrl) return;
    const normalizedPath = String(fileUrl)
      .replace(/\\\\/g, "/")
      .replace(/\\/g, "/")
      .replace(/\/\//g, "/");
    const fullUrl = `https://back.mediwave.tn/api/${normalizedPath}`;
    const link    = document.createElement("a");
    link.href     = fullUrl;
    link.download = doc?.nom_fichier || "document";
    link.click();
  }, []);

  const handleBack = useCallback(() => {
    navigate(medicalrecordRoot, { replace: true });
  }, [navigate, medicalrecordRoot]);

  // ─── Guards UI ────────────────────────────────────────────────────────────
  if (!patientId) {
    return (
      <AppLayoutDoctor>
        <div style={{ padding: 20 }}>
          <Alert
            message="Missing Patient ID"
            description="No patient ID provided in the route."
            type="warning"
            showIcon
          />
        </div>
      </AppLayoutDoctor>
    );
  }

  if (!selectedRecord) {
    return (
      <AppLayoutDoctor>
        <div style={{ textAlign: "center", padding: 50 }}>
          <Spin size="large" />
        </div>
      </AppLayoutDoctor>
    );
  }

  return (
    <AppLayoutDoctor>
      <div className="profile">
        <div className="record-type-page">
          <DoctorScrollTabs
            patientId={patientId}
            activeKey={activeKey}
            forceActiveKey={forcedTabKey}
            onChange={(href) => {
              navigate(href);
            }}
          />

          <div className="rtp-content">
            <div className="rtp-body">

              {/* ── Left Panel ── */}
              <div className="card">
                <RecordListPanel
                  selectedRecord={selectedRecord}
                  items={documentsByType}
                  loading={documentsByTypeLoading}
                  onItemClick={handleDocumentClick}
                  onDelete={handleDelete}
                  onBack={handleBack}
                  viewMode="doctor"
                  isDoctor={true}
                  subjectPatientId={patientId}
                  canArchive={canArchiveDocument}
                />
              </div>

              {/* ── Right Panel ── */}
              <div className="card rtp-viewer">
                <RecordViewerPanel
                  title={selectedRecord.label}
                  selected={selectedDocument}
                  onDelete={canArchiveDocument ? handleDelete : undefined}
                  patientId={patientId}
                  canUpdate={canUpdateDocument}
                  canArchive={canArchiveDocument}
                  onShare={() => message.info("Share feature coming soon")}
                  onPrint={() => window.print()}
                  onAnalyseAI={() => message.info("AI Analysis coming soon")}
                  onDownload={handleDownload}
                />
              </div>

            </div>
          </div>
        </div>
      </div>
    </AppLayoutDoctor>
  );
}
